import React from 'react'
import { Box, Typography } from '@mui/material'
import { makeStyles } from 'tss-react/mui'
import { Trans } from 'gatsby-plugin-react-i18next'
import SvgTrustPilotLogo from '../../images/trust-pilot-logo.inline.svg'
import SvgTrustPilotLogoNew from '../../images/trust-pilot-logo.inline.svg'
import { TrustpilotCompanyData } from '@talentinc/gatsby-source-trustpilot-widget'
import { TrustPilotRatingWidgetVariants } from '../../types/page'
import TrustPilotStarRating from './TrustPilotStarRating'
import { Grid } from '@mui/material'

interface Props {
  trustPilot: TrustpilotCompanyData | null
  variant?: TrustPilotRatingWidgetVariants | null
}

const RatingWidget: React.FC<Props> = ({ trustPilot, variant }) => {
  const { classes, cx } = useStyles()

  if (!trustPilot) {
    return null
  }

  const isQualitativePricingPage =
    variant === TrustPilotRatingWidgetVariants.QualitativePricingPage

  const isFiveStarsAlternative =
    variant === TrustPilotRatingWidgetVariants.FiveStarsAlternative

  if (variant === TrustPilotRatingWidgetVariants.FiveStarsDescriptive) {
    return (
      <Box className={classes.descriptiveVariantColumn}>
        <Typography component="span" className={classes.ratingBold}>
          {trustPilot.starsString}
        </Typography>
        <Typography
          className={cx(classes.typography, classes.typographyQualitativeDescriptive)}
          variant="body2"
        >
          <Trans
            i18nKey="trustpilot.ratingQualitativeMobile"
            values={{
              amount:
                trustPilot.businessUnit.numberOfReviews.total.toLocaleString(),
            }}
            components={{
              /* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content */
              strong: <span />,
            }}
          />
        </Typography>
        <SvgTrustPilotLogoNew
          className={cx({
            [classes.logo]: true,
          })}
        />
      </Box>
    )
  }

  return (
    <Box>
      {!isQualitativePricingPage && (
        <Box
          className={cx({
            [classes.box]: !isFiveStarsAlternative,
            [classes.boxAlternative]: isFiveStarsAlternative,
            [classes.boxFiveStars]:
              variant === TrustPilotRatingWidgetVariants.FiveStars,
            [classes.critiquePage]:
              variant ===
              TrustPilotRatingWidgetVariants.QuantitativePricingPageCritique,
          })}
          component="a"
          //@ts-ignore
          href={trustPilot.links.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {(!variant ||
            variant === TrustPilotRatingWidgetVariants.Quantitative) && (
            <Typography className={classes.typography} variant="body2">
              <Trans
                i18nKey="trustpilot.rating"
                values={{
                  rating: trustPilot.businessUnit.trustScore,
                  amount:
                    trustPilot.businessUnit.numberOfReviews.total.toLocaleString(),
                }}
                components={{
                  /* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content */
                  strong: <strong />,
                }}
              />
            </Typography>
          )}
          {variant === TrustPilotRatingWidgetVariants.Qualitative && (
            <Box className={classes.qualitative}>
              <Typography
                className={cx(
                  classes.typography,
                  classes.typographyQualitative,
                  classes.strong
                )}
                variant="body2"
              >
                {trustPilot.starsString}
              </Typography>
              <TrustPilotStarRating
                boxClass={classes.starsRating}
                iconClass={classes.starIcon}
                rating={trustPilot.businessUnit.trustScore}
              />
              <Typography
                className={cx(
                  classes.typography,
                  classes.typographyQualitative,
                  classes.desktop
                )}
                variant="body2"
              >
                <Trans
                  i18nKey="trustpilot.ratingQualitative"
                  values={{
                    amount:
                      trustPilot.businessUnit.numberOfReviews.total.toLocaleString(),
                  }}
                  components={{
                    /* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content */
                    strong: <strong />,
                  }}
                />
              </Typography>
              <Typography
                className={cx(
                  classes.typography,
                  classes.typographyQualitative,
                  classes.mobile
                )}
                variant="body2"
              >
                <Trans
                  i18nKey="trustpilot.ratingQualitativeMobile"
                  values={{
                    amount:
                      trustPilot.businessUnit.numberOfReviews.total.toLocaleString(),
                  }}
                  components={{
                    /* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content */
                    strong: <strong />,
                  }}
                />
              </Typography>
            </Box>
          )}
          {variant === TrustPilotRatingWidgetVariants.FiveStars && (
            <Typography className={classes.typography} variant="body2">
              <Trans
                i18nKey="trustpilot.fiveStarsNormal"
                values={{
                  reviews:
                    Math.floor(
                      trustPilot.businessUnit.numberOfReviews.fiveStars / 50
                    ) * 50,
                }}
              />
            </Typography>
          )}
          {variant === TrustPilotRatingWidgetVariants.FiveStarsAlternative && (
            <Typography className={classes.typography} variant="body2">
              <Trans
                i18nKey="trustpilot.fiveStarsNormal"
                values={{
                  reviews:
                    Math.floor(
                      trustPilot.businessUnit.numberOfReviews.fiveStars / 50
                    ) * 50,
                }}
              />
            </Typography>
          )}
          {variant ===
            TrustPilotRatingWidgetVariants.QuantitativePricingPageCritique && (
            <>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="3px"
              >
                <Typography
                  variant="inherit"
                  className={cx(classes.softSubtitle)}
                >
                  <Trans
                    i18nKey="trustpilot.ratingQualitativeMobile"
                    values={{
                      amount:
                        trustPilot.businessUnit.numberOfReviews.total.toLocaleString(),
                    }}
                    components={{
                      /* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content */
                      strong: <strong />,
                    }}
                  />
                </Typography>
              </Box>
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                gap="3px"
              >
                <Typography
                  className={cx(
                    classes.typography,
                    classes.typographyQualitative,
                    classes.strong
                  )}
                  variant="inherit"
                >
                  {trustPilot.starsString}
                </Typography>
                <TrustPilotStarRating
                  boxClass={classes.stars}
                  rating={trustPilot.businessUnit.trustScore}
                />
              </Box>
            </>
          )}
          <SvgTrustPilotLogo
            className={cx({
              [classes.logo]: true,
              [classes.logoFiveStars]:
                variant === TrustPilotRatingWidgetVariants.FiveStars,
            })}
          />
        </Box>
      )}
      {isQualitativePricingPage && (
        <Box
          className={cx({
            [classes.qppcontainer]: true,
          })}
          component="a"
          //@ts-ignore
          href={trustPilot.links.profileUrl}
          target="_blank"
          rel="noopener noreferrer"
        >
          {variant ===
            TrustPilotRatingWidgetVariants.QualitativePricingPage && (
            <Grid container className={classes.boxqualitativepricingpage}>
              <Grid item xs={12} sm={5} md={5} lg={5}>
                <Box display="flex" className={classes.ratingContentLeft}>
                  <Typography component="span" className={classes.ratingBold}>
                    <strong>{trustPilot.starsString}</strong>
                  </Typography>
                  <TrustPilotStarRating
                    boxClass={classes.stars}
                    // iconClass={classes.starIcon}
                    rating={trustPilot.businessUnit.trustScore}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={7} md={7} lg={7}>
                <Box display="flex" className={classes.ratingContentRight}>
                  <Typography
                    className={cx(
                      classes.typography,
                      classes.typographyQualitativePricingPage,
                      classes.desktop
                    )}
                    variant="body2"
                  >
                    <Trans
                      i18nKey="trustpilot.ratingQualitative"
                      values={{
                        amount:
                          trustPilot.businessUnit.numberOfReviews.total.toLocaleString(),
                      }}
                      components={{
                        /* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content */
                        strong: <strong />,
                      }}
                    />
                  </Typography>
                  <Typography
                    className={cx(
                      classes.typography,
                      classes.typographyQualitativePricingPage,
                      classes.mobile
                    )}
                    variant="body2"
                  >
                    <Trans
                      i18nKey="trustpilot.ratingQualitativeMobile"
                      values={{
                        amount:
                          trustPilot.businessUnit.numberOfReviews.total.toLocaleString(),
                      }}
                      components={{
                        /* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/anchor-has-content */
                        strong: <strong />,
                      }}
                    />
                  </Typography>
                  <SvgTrustPilotLogoNew
                    className={cx({
                      [classes.logo]: true,
                    })}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </Box>
      )}
    </Box>
  )
}

const useStyles = makeStyles()((theme) => ({
  ratingContentLeft: {
    paddingBottom: '4px',
    justifyContent: 'right',
    alignItems: 'center',
    paddingLeft: '22px',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
      paddingLeft: '0px',
    },
  },
  ratingContentRight: {
    paddingBottom: '4px',
    justifyContent: 'left',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      justifyContent: 'center',
    },
  },
  ratingBold: {
    fontSize: '17px',
    color: '#202124',
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      paddingLeft: `${theme.spacing(1)}px`,
      paddingRight: `${theme.spacing(1)}px`,
    },
  },
  stars: {
    paddingTop: `${theme.spacing(0.7)}px`,
    paddingLeft: `${theme.spacing(0.5)}px`,
    marginRight: `${theme.spacing(1)}px`,
    '& svg': {
      height: '22px',
      width: 'auto',
    },
  },
  boxqualitativepricingpage: {
    width: '100%!important',
    alignItems: 'center',
    justifyContent: 'center',
  },
  qppcontainer: {
    width: '100%!important',
    alignItems: 'center',
    justifyContent: 'center',
    textDecoration: 'none',
    color: 'inherit',
  },
  box: {
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    textDecoration: 'none',
    [theme.breakpoints.down('md')]: {
      alignItems: 'center',
      flexDirection: 'column-reverse',
    },
    color: 'inherit',
    '& > p': {
      color: 'inherit',
    },
  },
  boxFiveStars: {
    [theme.breakpoints.down('md')]: {
      alignItems: 'flex-end',
      flexDirection: 'row',
    },
  },
  boxAlternative: {
    justifyContent: 'left',
    border: '1px solid #C3E5FE',
    width: '295px',
    padding: '8px 12px 8px 18px',
    borderRadius: '8px',
    textDecoration: 'none',
    alignItems: 'flex-end',
    display: 'flex',
    flexDirection: 'row',
    position: 'relative',
    zIndex: 3,
    color: 'inherit',
    [theme.breakpoints.down('md')]: {
      justifyContent: 'center!important',
      margin: 'auto',
    },
    '& > p': {
      color: 'inherit',
    },
  },
  critiquePage: {
    flexDirection: 'column-reverse',
    alignItems: 'center',
  },
  softSubtitle: {
    fontSize: 16,
    lineHeight: '20px',
    fontWeight: 400,
    color: theme.colors.neutral[50],
    fontFamily: 'tt-talent',
  },
  desktop: {
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },
  logo: {
    color: 'inherit',
    width: 'auto',
    height: '1.6em',
    marginLeft: '.25rem',
    [theme.breakpoints.down('md')]: {
      marginLeft: '0',
    },
  },
  logoFiveStars: {
    [theme.breakpoints.down('md')]: {
      marginBottom: '.1rem',
      marginLeft: '.25rem',
    },
  },
  mobile: {
    display: 'none',
    [theme.breakpoints.down('md')]: {
      display: 'initial',
    },
  },
  qualitative: {
    alignItems: 'flex-end',
    display: 'flex',
  },
  strong: {
    fontWeight: 700,
  },
  starsRating: {
    margin: '0 .5rem -.2rem',
    [theme.breakpoints.down('md')]: {
      margin: '0 .2rem -.2rem',
    },
  },
  descriptiveVariantColumn: {
    display: 'flex',
    flexDirection: 'column',
    paddingTop: '4.19em',
    [theme.breakpoints.down('md')]: {
      display: 'flex',
      flexDirection: 'column',
      paddingTop: '28px',
      paddingBottom: '40px',
    },
  },
  starIcon: {
    [theme.breakpoints.down('sm')]: {
      height: '1rem',
    },
  },
  typography: {
    fontSize: '1.0625rem',
    margin: '0',
    textDecoration: 'none',
  },
  typographyQualitativeDescriptive: {
    color: theme.colors.neutral[50],
    fontSize: 16,
  },
  typographyQualitative: {
    [theme.breakpoints.down('sm')]: {
      fontSize: '.88rem',
    },
  },
  typographyQualitativePricingPage: {
    paddingTop: '4px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1rem',
      paddingTop: '0',
    },
  },
}))

export default RatingWidget
